import React, { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react'
import { App } from 'antd'
import type { ArgsProps } from 'antd/es/notification/interface'
import i18next from 'i18next'

export enum TOAST_TYPES {
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  IONIC = 'ionic',
  IONIC_HIDE = 'ionicHidden',
}

interface ToastConfig extends Pick<ArgsProps, 'description' | 'message'> {
  type: TOAST_TYPES
}

interface IonToastConfig {
  message?: string
  show: boolean
}

const defaultToast: ToastConfig = {
  description: i18next.t('toasts.defaults.error.description'),
  message: i18next.t('toasts.defaults.error.message'),
  type: TOAST_TYPES.ERROR,
}

const ionDefaultToast: IonToastConfig = {
  show: false,
}

export interface ToastContextProps {
  ionToast: IonToastConfig
  setIonToast: Dispatch<SetStateAction<IonToastConfig>>
  setToast: Dispatch<SetStateAction<ToastConfig>>
  toast: ToastConfig
}

export const ToastContext = React.createContext<ToastContextProps>({
  ionToast: ionDefaultToast,
  setIonToast: () => undefined,
  setToast: () => undefined,
  toast: defaultToast,
})

interface ToastProviderProps {
  children?: React.ReactNode
}

export const ToastProvider: FC<ToastProviderProps> = ({ children }) => {
  const { notification } = App.useApp()

  const [toast, setToast] = useState<ToastConfig>(defaultToast)
  const [ionToast, setIonToast] = useState<IonToastConfig>(ionDefaultToast)

  const firstUpdate = useRef(true)

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    notification[toast.type]({
      title: toast.message,
      description: toast.description,
    })
  }, [toast, notification])

  return <ToastContext.Provider value={{ setToast, toast, ionToast, setIonToast }}>{children}</ToastContext.Provider>
}
